import { Container } from '@/components/ui/Container'
import { createIcon, IconPaths } from '@/utils/icons'
import { Animations } from '@/utils/styles'
import { t } from '@/utils/i18n'

interface ProjectData {
  tag: string
  tagColor: string
  title: string
  location: string
  description: string
  stats: { number: string; label: string }[]
  highlights: string[]
  href: string
}

function getProjectsData(): ProjectData[] {
  return [
    {
      tag: t('projectsSection.project1.tag'),
      tagColor: 'bg-green-600',
      title: t('projectsSection.project1.title'),
      location: t('projectsSection.project1.location'),
      description: t('projectsSection.project1.description'),
      stats: [
        { number: t('projectsSection.project1.stat1Number'), label: t('projectsSection.project1.stat1Label') },
        { number: t('projectsSection.project1.stat2Number'), label: t('projectsSection.project1.stat2Label') },
        { number: t('projectsSection.project1.stat3Number'), label: t('projectsSection.project1.stat3Label') },
      ],
      highlights: [
        t('projectsSection.project1.highlight1'),
        t('projectsSection.project1.highlight2'),
        t('projectsSection.project1.highlight3'),
      ],
      href: '/projects/koica-cts-seed-1',
    },
    {
      tag: t('projectsSection.project2.tag'),
      tagColor: 'bg-blue-600',
      title: t('projectsSection.project2.title'),
      location: t('projectsSection.project2.location'),
      description: t('projectsSection.project2.description'),
      stats: [
        { number: t('projectsSection.project2.stat1Number'), label: t('projectsSection.project2.stat1Label') },
        { number: t('projectsSection.project2.stat2Number'), label: t('projectsSection.project2.stat2Label') },
        { number: t('projectsSection.project2.stat3Number'), label: t('projectsSection.project2.stat3Label') },
      ],
      highlights: [
        t('projectsSection.project2.highlight1'),
        t('projectsSection.project2.highlight2'),
        t('projectsSection.project2.highlight3'),
      ],
      href: '/projects/koica-cts-seed-2',
    },
    {
      tag: t('projectsSection.project3.tag'),
      tagColor: 'bg-blue-600',
      title: t('projectsSection.project3.title'),
      location: t('projectsSection.project3.location'),
      description: t('projectsSection.project3.description'),
      stats: [
        { number: t('projectsSection.project3.stat1Number'), label: t('projectsSection.project3.stat1Label') },
        { number: t('projectsSection.project3.stat2Number'), label: t('projectsSection.project3.stat2Label') },
        { number: t('projectsSection.project3.stat3Number'), label: t('projectsSection.project3.stat3Label') },
      ],
      highlights: [
        t('projectsSection.project3.highlight1'),
        t('projectsSection.project3.highlight2'),
        t('projectsSection.project3.highlight3'),
      ],
      href: '/projects/deeptech-tips',
    },
  ]
}

export function ProjectsSection(): HTMLElement {
  const section = document.createElement('section')
  section.className = 'section-container bg-slate-50'

  const container = Container({
    children: [
      createSectionHeader(),
      createProjectsGrid(),
    ],
  })

  section.appendChild(container)
  return section
}

function createSectionHeader(): HTMLElement {
  const header = document.createElement('div')
  header.className = 'text-center mb-16'

  const title = document.createElement('h2')
  title.textContent = t('projectsSection.sectionTitle')
  title.className = 'text-4xl sm:text-5xl font-bold text-slate-900 mb-4'

  const subtitle = document.createElement('p')
  subtitle.textContent = t('projectsSection.sectionSubtitle')
  subtitle.className = 'text-xl text-slate-600'

  header.appendChild(title)
  header.appendChild(subtitle)
  return header
}

function createProjectsGrid(): HTMLElement {
  const grid = document.createElement('div')
  grid.className = 'grid grid-cols-1 lg:grid-cols-3 gap-8'

  const projectsData = getProjectsData()
  projectsData.forEach((project, index) => {
    const card = createProjectCard(project, index)
    grid.appendChild(card)
  })

  return grid
}

function createProjectCard(project: ProjectData, index: number): HTMLElement {
  const card = document.createElement('div')
  card.className = 'card overflow-hidden flex flex-col h-full animate-slide-up p-6'
  card.style.animationDelay = Animations.staggerDelay(index)

  // Tag
  const tag = document.createElement('span')
  tag.className = `inline-flex items-center gap-1.5 px-3 py-1.5 ${project.tagColor} text-white text-xs font-bold uppercase tracking-wider rounded-full w-fit mb-4`

  if (project.tag === 'Completed') {
    tag.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
      ${project.tag}
    `
  } else {
    tag.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke-width="2"/>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6l4 2"/>
      </svg>
      ${project.tag}
    `
  }

  // Title with location
  const titleGroup = document.createElement('div')
  titleGroup.className = 'space-y-2 mb-4'

  const title = document.createElement('h3')
  title.textContent = project.title
  title.className = 'text-xl font-bold text-slate-900'

  const location = document.createElement('div')
  location.className = 'flex items-center gap-2 text-slate-600 text-sm'
  const locationIcon = document.createElement('span')
  locationIcon.className = 'text-orange-600 w-4 h-4 flex-shrink-0'
  locationIcon.appendChild(createIcon(IconPaths.location))
  const locationText = document.createElement('span')
  locationText.textContent = project.location
  location.appendChild(locationIcon)
  location.appendChild(locationText)

  titleGroup.appendChild(title)
  titleGroup.appendChild(location)

  // Description
  const description = document.createElement('p')
  description.textContent = project.description
  description.className = 'text-slate-600 text-sm leading-relaxed mb-4 flex-grow'

  // Stats
  const stats = document.createElement('div')
  stats.className = 'grid grid-cols-3 gap-3 py-4 border-y border-slate-200 mb-4'

  project.stats.forEach(stat => {
    const statDiv = document.createElement('div')
    statDiv.className = 'text-center'

    const number = document.createElement('div')
    number.textContent = stat.number
    number.className = 'text-lg font-bold text-orange-600'

    const label = document.createElement('div')
    label.textContent = stat.label
    label.className = 'text-xs text-slate-600 mt-1'

    statDiv.appendChild(number)
    statDiv.appendChild(label)
    stats.appendChild(statDiv)
  })

  // Highlights
  const highlights = document.createElement('div')
  highlights.className = 'space-y-2 mb-6'

  const checkIconPath = IconPaths.check

  project.highlights.forEach(highlight => {
    const item = document.createElement('div')
    item.className = 'flex items-start gap-2'

    const icon = document.createElement('span')
    icon.className = 'text-green-600 flex-shrink-0 mt-0.5 w-4 h-4'
    icon.appendChild(createIcon(checkIconPath))

    const text = document.createElement('span')
    text.textContent = highlight
    text.className = 'text-xs text-slate-600'

    item.appendChild(icon)
    item.appendChild(text)
    highlights.appendChild(item)
  })

  // CTA Button
  const ctaButton = document.createElement('a')
  ctaButton.href = project.href
  ctaButton.textContent = t('projectsSection.viewDetails')
  ctaButton.className = 'btn-secondary inline-block text-center w-full text-sm'
  ctaButton.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: project.href }))
  })

  card.appendChild(tag)
  card.appendChild(titleGroup)
  card.appendChild(description)
  card.appendChild(stats)
  card.appendChild(highlights)
  card.appendChild(ctaButton)

  return card
}
